/**
 * Season utilities for the frontend
 * 
 * EPL season runs August to May:
 * - Aug-Dec: First half of season (e.g., 2025-26)
 * - Jan-May: Second half of season (e.g., 2025-26)
 * - June-July: Off-season (defaults to upcoming season)
 */

/**
 * Get the current season string (e.g., "2025-26")
 */
export function getCurrentSeason(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  
  if (month <= 4) {
    // Jan-May: season started previous year
    return `${year - 1}-${String(year).slice(2)}`;
  } else {
    // June-Dec: season starts this year
    return `${year}-${String(year + 1).slice(2)}`;
  }
}

/**
 * Get the API season year (e.g., "2025" for "2025-26")
 */
export function getApiSeasonYear(season?: string): string {
  const s = season || getCurrentSeason();
  const match = s.match(/^(\d{4})/);
  return match ? match[1] : String(new Date().getFullYear());
}

/**
 * Get the previous API season year for TDD eligibility.
 * Accepts any season format ("2025-26", "2026", "2026-Apertura") and
 * returns the numeric year minus 1 as a string.
 */
export function getPreviousSeasonYear(season?: string): string {
  const s = season || getCurrentSeason();
  const year = parseInt(getApiSeasonYear(s));
  return String(year - 1);
}

/**
 * Get the current season string for a specific competition based on its seasonFormat.
 * Supports cross-year ('YYYY-YY'), calendar-year ('YYYY'), and Apertura/Clausura ('YYYY-A/C').
 *
 * This is the single source of truth for frontend season calculation.
 * The backend equivalent lives in supabase/functions/_shared/api-football.ts.
 */
export function getCurrentSeasonForCompetition(seasonFormat: 'YYYY-YY' | 'YYYY' | 'YYYY-A/C'): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1 // 1-based

  switch (seasonFormat) {
    case 'YYYY-YY': {
      // Cross-year leagues (EPL, La Liga, etc.) — season starts ~August
      // Jan-May = second half of prev season. June+ = upcoming season.
      if (month <= 5) {
        const prevYear = year - 1
        const shortYear = year % 100
        return `${prevYear}-${String(shortYear).padStart(2, '0')}`
      } else {
        const nextYear = (year + 1) % 100
        return `${year}-${String(nextYear).padStart(2, '0')}`
      }
    }
    case 'YYYY':
      // Calendar-year leagues (MLS) — season runs ~Feb to ~Nov
      // Dec-Jan is off-season; use previous year's season
      if (month <= 1 || month === 12) {
        return String(month === 12 ? year : year - 1)
      }
      return String(year)
    case 'YYYY-A/C': {
      // Apertura/Clausura (Liga MX)
      if (month >= 7) {
        return `${year}-Apertura`
      } else {
        return `${year}-Clausura`
      }
    }
    default:
      return String(year)
  }
}

// Export constants for common use cases
// These are evaluated once at module load time
export const CURRENT_SEASON = getCurrentSeason();
export const CURRENT_API_YEAR = getApiSeasonYear();
export const TDD_ELIGIBILITY_SEASON = getPreviousSeasonYear();



